/**
 * Template Color Picker
 * Wrapper around the EmailColorPicker with template-specific description
 *
 * @file src/components/email/templates/TemplateColorPicker.tsx
 * @created 2025-10-22
 * @updated 2025-10-25 - Switched to email-specific color picker
 */

import React from 'react';
import EmailColorPicker from './EmailColorPicker';

interface TemplateColorPickerProps {
  label: string;
  description: string;
  value: string;
  onChange: (color: string) => void;
}

/**
 * TemplateColorPicker Component
 *
 * A wrapper around the EmailColorPicker component with additional description support
 * for template customization context.
 *
 * @example
 * <TemplateColorPicker
 *   label="Primary Color"
 *   description="Used for headers, buttons, and main elements"
 *   value={primaryColor}
 *   onChange={setPrimaryColor}
 * />
 */
const TemplateColorPicker: React.FC<TemplateColorPickerProps> = ({
  label,
  description,
  value,
  onChange
}) => {
  // Determine color type based on label
  const colorType = label.toLowerCase().includes('text') ? 'text' : 'brand';

  return (
    <div className="space-y-1">
      <EmailColorPicker
        label={label}
        value={value}
        onChange={onChange}
        colorType={colorType}
      />
      <p className="text-xs text-gray-600 pl-1">{description}</p>
    </div>
  );
};

export default TemplateColorPicker;
